import type { RuleSet } from '../rules/schema';
import { type Command, canAttack, type PlayerId, type SimState, type Stance } from '../sim';

/** Stances in the order the dock shows them. */
export const STANCES: readonly Stance[] = ['aggressive', 'hold', 'passive'];

/** Dock label and tooltip text per stance (what it does, plainly). */
export const STANCE_INFO: Record<Stance, { label: string; help: string }> = {
  aggressive: { label: 'Aggressive', help: 'Chase and fire at anything met' },
  hold: { label: 'Hold', help: 'Fire in range, never pursue' },
  passive: { label: 'Passive', help: 'Never engage; hits only what you order' },
};

/** The selected own units that fight (and so take a stance or an attack-move). */
export function fighterIds(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  selected: readonly string[]
): string[] {
  return selected.filter((id) => {
    const u = state.units[id];
    return !!u && u.owner === playerId && !u.inside && canAttack(rules, u);
  });
}

/** A unit's stance, with aggressive (stored as absent) made explicit. */
const stanceOf = (state: SimState, id: string): Stance => state.units[id]?.stance ?? 'aggressive';

/** The stance every one of `ids` shares, or null when mixed (or none). */
export function commonStance(state: SimState, ids: readonly string[]): Stance | null {
  if (ids.length === 0) return null;
  const first = stanceOf(state, ids[0]);
  return ids.every((id) => stanceOf(state, id) === first) ? first : null;
}

/**
 * Whether the A key belongs to attack-move rather than the camera's pan-left:
 * own fighters are selected, or a targeting mode is armed. Arrow-left always pans.
 */
export function claimsAKey(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  selected: readonly string[],
  targeting: boolean
): boolean {
  return targeting || fighterIds(state, rules, playerId, selected).length > 0;
}

/** The `setStance` command for the fighters among `selected`, or null when there are none or they all have it already. */
export function stanceCommand(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  selected: readonly string[],
  stance: Stance
): Command | null {
  const ids = fighterIds(state, rules, playerId, selected);
  if (ids.length === 0 || commonStance(state, ids) === stance) return null;
  return { type: 'setStance', unitIds: ids, stance };
}

/** H: hold unless every fighter already holds, then back to aggressive. */
export function toggleHoldCommand(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  selected: readonly string[]
): Command | null {
  const ids = fighterIds(state, rules, playerId, selected);
  if (ids.length === 0) return null;
  const allHold = ids.every((id) => stanceOf(state, id) === 'hold');
  return { type: 'setStance', unitIds: ids, stance: allHold ? 'aggressive' : 'hold' };
}

/** The `attackMove` command for the fighters among `selected`, or null when there are none. */
export function attackMoveCommand(
  state: SimState,
  rules: RuleSet,
  playerId: PlayerId,
  selected: readonly string[],
  target: { q: number; r: number }
): Command | null {
  const ids = fighterIds(state, rules, playerId, selected);
  return ids.length === 0 ? null : { type: 'attackMove', unitIds: ids, target };
}
